import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronRight, ChevronLeft, Info, ShoppingCart, Minus, Plus, Package2, Wine, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { eur } from '@/lib/format';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/Header';
import { mergeCategoryLabels } from '@/lib/categoryLabels';
import Seo from '@/components/Seo';

const STEPS_META = [
  { key: 'size',    fallback: 'Taglia',    type: 'radio' },
  { key: 'base',    fallback: 'Base',      type: 'multi', limitKey: 'max_bases' },
  { key: 'protein', fallback: 'Proteine',  type: 'multi', limitKey: 'max_proteins' },
  { key: 'topping', fallback: 'Topping',   type: 'multi', limitKey: 'max_toppings' },
  { key: 'sauce',   fallback: 'Salse',     type: 'multi', limitKey: 'max_sauces' },
  { key: 'extra',   fallback: 'Extra',     type: 'multi', limitKey: 'max_extras' },
];

const CATEGORY_LABELS_DEFAULT = { base: 'Base', protein: 'Proteine', topping: 'Topping', sauce: 'Salse', extra: 'Extra' };

export default function BuilderPage() {
  const [ingredients, setIngredients] = useState([]);
  const [settings, setSettings] = useState(null);

  // Tell the install-banner popup to sit above the Builder's fixed bottom bar.
  useEffect(() => {
    document.body.classList.add('has-builder-bar');
    return () => document.body.classList.remove('has-builder-bar');
  }, []);
  // Selection uses arrays of ids; duplicates represent quantity (backward-compatible)
  const [selection, setSelection] = useState({ size: null, base: [], protein: [], topping: [], sauce: [], extra: [] });
  const [notes, setNotes] = useState('');
  const [stepIdx, setStepIdx] = useState(0);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const addCart = useCart(s => s.add);
  const replaceCart = useCart(s => s.replace);
  const findCartItem = useCart(s => s.findById);

  // Edit mode: /builder?edit={cartItemId} — pre-fill the form with a
  // previously composed poke from the cart. Saving replaces the line
  // instead of adding a new one. Only 'custom' items can be edited.
  const editId = searchParams.get('edit');
  const [editingItem, setEditingItem] = useState(null); // the cart line being edited

  // Combo mode: if ?combo=<id>, load the combo and lock size
  const comboId = searchParams.get('combo');
  const [combo, setCombo] = useState(null);
  const [drinks, setDrinks] = useState([]);
  const [drinkDialog, setDrinkDialog] = useState(false);
  const [selectedDrinkId, setSelectedDrinkId] = useState('');

  useEffect(() => {
    api.get('/ingredients').then(r => setIngredients(r.data));
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    if (comboId) {
      Promise.all([
        api.get('/combos').then(r => r.data.find(c => c.id === comboId)),
        api.get('/ingredients?category=beverage').then(r => r.data),
      ]).then(([c, d]) => {
        if (!c) { toast.error('Menù non trovato'); return; }
        setCombo(c);
        setDrinks(d);
        // Preselect the size defined by the combo, and lock it
        if (c.size_id) {
          setSelection(prev => ({ ...prev, size: c.size_id }));
          setStepIdx(1);  // skip past size step
        }
      }).catch(() => toast.error('Errore caricamento menù'));
    }
  }, [comboId]);

  // Load the cart line when in edit mode: pre-fill selection, notes, jump
  // directly to the summary step. If the id is not a valid 'custom' item
  // we silently drop the edit query and start a fresh composition.
  useEffect(() => {
    if (!editId) return;
    const line = findCartItem(editId);
    if (!line || line.kind !== 'custom') return;
    setEditingItem(line);
    setSelection({
      size: line.size_id || null,
      base: [...(line.base_ids || [])],
      protein: [...(line.protein_ids || [])],
      topping: [...(line.topping_ids || [])],
      sauce: [...(line.sauce_ids || [])],
      extra: [...(line.extra_ids || [])],
    });
    setNotes(line.notes || '');
    // Jump directly to the summary/last step so users can tweak from there
    setStepIdx(STEPS_META.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Build merged labels + STEPS (with dynamic titles)
  const labels = useMemo(() => mergeCategoryLabels(settings?.category_labels), [settings]);
  const CATEGORY_LABELS = useMemo(() => ({
    base: labels.base, protein: labels.protein, topping: labels.topping, sauce: labels.sauce, extra: labels.extra,
  }), [labels]);
  const STEPS = useMemo(() => [
    ...STEPS_META.map(s => ({ ...s, title: labels[s.key] || s.fallback })),
    { key: 'summary', title: 'Riepilogo', type: 'summary' },
  ], [labels]);

  const byCategory = useMemo(() => {
    const m = {};
    ingredients.forEach(i => { (m[i.category] = m[i.category] || []).push(i); });
    return m;
  }, [ingredients]);

  const step = STEPS[stepIdx];
  const selectedSize = ingredients.find(i => i.id === selection.size);

  const totalUnits = useMemo(() => {
    return ['base', 'protein', 'topping', 'sauce', 'extra']
      .reduce((s, c) => s + selection[c].length, 0);
  }, [selection]);

  const rawTotalPrice = useMemo(() => {
    let total = selectedSize?.price || 0;
    ['base', 'protein', 'topping', 'sauce', 'extra'].forEach(cat => {
      selection[cat].forEach(id => {
        const ing = ingredients.find(i => i.id === id);
        if (ing) total += ing.price;
      });
    });
    return total;
  }, [selection, ingredients, selectedSize]);

  // In combo mode, show fixed combo price instead of computed total
  const totalPrice = combo ? Number(combo.price) : rawTotalPrice;

  const catLimit = (cat) => {
    const k = STEPS.find(s => s.key === cat)?.limitKey;
    if (!k || !selectedSize) return null;
    const v = selectedSize[k];
    return v == null ? null : Number(v);
  };
  const totalLimit = selectedSize?.max_total == null ? null : Number(selectedSize.max_total);
  const maxQtyPerItem = selectedSize?.max_qty_per_item == null ? 1 : Math.max(1, Number(selectedSize.max_qty_per_item));

  const qtyOf = (cat, id) => selection[cat].filter(x => x === id).length;
  const catUnits = (cat) => selection[cat].length;

  const addOne = (cat, id) => {
    const current = qtyOf(cat, id);
    if (current >= maxQtyPerItem) {
      toast.error(`Puoi aggiungere lo stesso ingrediente al massimo ${maxQtyPerItem} ${maxQtyPerItem === 1 ? 'volta' : 'volte'}`);
      return;
    }
    const limit = catLimit(cat);
    if (limit != null && catUnits(cat) >= limit) {
      toast.error(`Massimo ${limit} ${(CATEGORY_LABELS[cat] || cat).toLowerCase()} per questa taglia`);
      return;
    }
    if (totalLimit != null && totalUnits >= totalLimit) {
      toast.error(`Hai raggiunto il limite di ${totalLimit} ingredienti per questa poke`);
      return;
    }
    setSelection(prev => ({ ...prev, [cat]: [...prev[cat], id] }));
  };

  const removeOne = (cat, id) => {
    setSelection(prev => {
      const arr = prev[cat];
      const idx = arr.lastIndexOf(id);
      if (idx === -1) return prev;
      const next = [...arr.slice(0, idx), ...arr.slice(idx + 1)];
      return { ...prev, [cat]: next };
    });
  };

  const canNext = () => {
    if (step.type === 'radio') return !!selection[step.key];
    if (step.type === 'multi') return true;
    return true;
  };

  const buildOrderItem = () => {
    // Aggregate quantities for a cleaner summary line
    const summarize = (cat) => {
      const counts = new Map();
      selection[cat].forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
      return [...counts.entries()].map(([id, q]) => {
        const ing = ingredients.find(i => i.id === id);
        if (!ing) return null;
        return q > 1 ? `${q}× ${ing.name}` : ing.name;
      }).filter(Boolean);
    };

    const componentsSummary = [];
    if (selectedSize) componentsSummary.push(`Taglia: ${selectedSize.name}`);
    ['base', 'protein', 'topping', 'sauce', 'extra'].forEach(cat => {
      const list = summarize(cat);
      if (list.length) componentsSummary.push(`${CATEGORY_LABELS[cat] || cat}: ${list.join(', ')}`);
    });

    return {
      kind: 'custom',
      name: `Poke ${selectedSize?.name || 'Custom'}`,
      unit_price: Number(totalPrice.toFixed(2)),
      quantity: 1,
      size_id: selection.size,
      size_name: selectedSize?.name,
      base_ids: selection.base,
      protein_ids: selection.protein,
      topping_ids: selection.topping,
      sauce_ids: selection.sauce,
      extra_ids: selection.extra,
      components_summary: componentsSummary,
      notes,
    };
  };

  const handleAddToCart = () => {
    if (!selection.size) return toast.error('Seleziona la taglia');
    if (selection.base.length === 0) return toast.error('Seleziona almeno una base');
    if (selection.protein.length === 0) return toast.error('Seleziona almeno una proteina');
    // Combo mode: open drink selection dialog first
    if (combo && combo.drink_ids?.length > 0) {
      setSelectedDrinkId(combo.drink_ids[0]);
      setDrinkDialog(true);
      return;
    }
    const payload = buildOrderItem();
    if (editingItem) {
      // Preserve original quantity when replacing an existing cart line.
      payload.quantity = editingItem.quantity || 1;
      replaceCart(editingItem.id, payload);
      toast.success('Poke aggiornata nel carrello!');
    } else {
      addCart(payload);
      toast.success('Poke aggiunta al carrello!');
    }
    nav('/cart');
  };

  const confirmComboAddToCart = () => {
    if (!combo) return;
    if (!selectedDrinkId) return toast.error('Seleziona una bevanda');
    const drink = drinks.find(d => d.id === selectedDrinkId);
    const item = buildOrderItem();
    // Override with combo details: name, price, drink
    item.name = combo.name;
    item.kind = 'combo';
    item.combo_id = combo.id;
    item.drink_id = drink?.id;
    item.unit_price = Number(combo.price.toFixed(2));
    if (drink) item.components_summary.push(`Bevanda: ${drink.name}`);
    if (editingItem) {
      item.quantity = editingItem.quantity || 1;
      replaceCart(editingItem.id, item);
      toast.success(`${combo.name} aggiornato!`);
    } else {
      addCart(item);
      toast.success(`${combo.name} aggiunto al carrello!`);
    }
    setDrinkDialog(false);
    nav('/cart');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Componi la tua Poke Bowl — Builder"
        description="Crea la tua poke bowl personalizzata a Catania: scegli base, proteine, topping e salse. Consegna a domicilio o ritiro in negozio."
        path="/builder"
        keywords="componi poke, poke bowl personalizzata, builder poke catania"
      />
      <PublicHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-6 w-full">
        <div className="mb-4">
          {editingItem && (
            <div className="rounded-2xl bg-[--brand-primary]/10 border-2 border-[--brand-primary]/30 px-4 py-3 mb-4 flex items-center gap-3" data-testid="builder-edit-banner">
              <Pencil size={18} className="text-[--brand-primary] shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-[--brand-primary] font-semibold">Modifica poke</div>
                <div className="text-sm text-[--brand-text]">Stai modificando una poke gi&agrave; nel carrello. Salvando la sostituirai.</div>
              </div>
            </div>
          )}
          {combo && (
            <div className="rounded-2xl bg-[--brand-accent]/10 border-2 border-[--brand-accent]/30 px-4 py-3 mb-4 flex items-center gap-3" data-testid="builder-combo-banner">
              <Package2 size={20} className="text-[--brand-accent] shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-[--brand-accent] font-semibold">Menù combo</div>
                <div className="font-display font-bold text-[--brand-text]">{combo.name} — {eur(combo.price)}</div>
                <div className="text-xs text-[--brand-muted] mt-0.5">Componi la tua poke {combo.size_id ? ingredients.find(i => i.id === combo.size_id)?.name : ''} + bevanda a scelta</div>
              </div>
            </div>
          )}
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Crea la tua Poke</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Passo {stepIdx + 1} di {STEPS.length}: {step.title}</h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-12 pb-56 lg:pb-6">
          <div className="lg:col-span-7 space-y-4">
            <Progress value={((stepIdx + 1) / STEPS.length) * 100} className="h-2" />

            <div className="flex overflow-x-auto gap-2 hide-scrollbar">
              {STEPS.map((s, i) => {
                if (combo && s.key === 'size') return null; // taglia bloccata in modalità combo
                return (
                  <button
                    key={s.key}
                    onClick={() => setStepIdx(i)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${i === stepIdx ? 'bg-[--brand-primary] text-white border-[--brand-primary]' : i < stepIdx ? 'bg-[--brand-surface-2] text-[--brand-primary] border-[--brand-border]' : 'bg-white text-[--brand-muted] border-[--brand-border]'}`}
                  >
                    {i < stepIdx && <Check size={12} className="inline mr-1" />}
                    {s.title}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step.key}
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                {step.type === 'summary' ? (
                  <SummaryStep
                    selectedSize={selectedSize}
                    selection={selection}
                    ingredients={ingredients}
                    totalPrice={totalPrice}
                    notes={notes} setNotes={setNotes}
                    labels={CATEGORY_LABELS}
                  />
                ) : step.type === 'radio' ? (
                  <SizeGrid
                    items={(byCategory[step.key] || []).sort((a,b)=>a.sort_order-b.sort_order)}
                    selectedId={selection[step.key]}
                    onPick={(id) => setSelection(prev => ({ ...prev, [step.key]: id }))}
                  />
                ) : (
                  <MultiGrid
                    items={(byCategory[step.key] || []).sort((a,b)=>a.sort_order-b.sort_order)}
                    cat={step.key}
                    labels={CATEGORY_LABELS}
                    qtyOf={(id) => qtyOf(step.key, id)}
                    onAdd={(id) => addOne(step.key, id)}
                    onRemove={(id) => removeOne(step.key, id)}
                    limit={catLimit(step.key)}
                    used={catUnits(step.key)}
                    maxQtyPerItem={maxQtyPerItem}
                    totalLimit={totalLimit}
                    totalUnits={totalUnits}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-between pt-2">
              <Button variant="secondary" className="rounded-xl" disabled={stepIdx === 0 || (combo && stepIdx <= 1)}
                      onClick={() => setStepIdx(i => Math.max(combo ? 1 : 0, i - 1))}
                      data-testid="builder-prev-step-button">
                <ChevronLeft size={16} className="mr-1"/>Indietro
              </Button>
              {stepIdx < STEPS.length - 1 ? (
                <Button className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]"
                        disabled={!canNext()}
                        onClick={() => setStepIdx(i => Math.min(STEPS.length - 1, i + 1))}
                        data-testid="builder-next-step-button">
                  Avanti <ChevronRight size={16} className="ml-1"/>
                </Button>
              ) : (
                <Button className="rounded-xl bg-[--brand-accent] hover:bg-[--brand-accent-hover] text-white"
                        onClick={handleAddToCart}
                        data-testid="builder-add-to-cart-button">
                  {editingItem ? (
                    <><Pencil size={16} className="mr-2"/>Salva modifiche</>
                  ) : (
                    <><ShoppingCart size={16} className="mr-2"/>Aggiungi al carrello</>
                  )}
                </Button>
              )}
            </div>
          </div>

          <aside className="hidden lg:block lg:col-span-5">
            <div className="sticky top-20">
              <SummaryCard selectedSize={selectedSize} selection={selection} ingredients={ingredients} totalPrice={totalPrice} totalUnits={totalUnits} totalLimit={totalLimit} labels={CATEGORY_LABELS}/>
            </div>
          </aside>
        </div>

        {/* Mobile sticky summary */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[--brand-border] px-4 py-3 flex items-center gap-3 shadow-lg z-30">
          <div className="flex-1">
            <div className="text-xs text-[--brand-muted]">Totale {totalLimit ? `· ${totalUnits}/${totalLimit}` : ''}</div>
            <div className="font-display text-xl font-bold tabular-nums" data-testid="builder-total-price">{eur(totalPrice)}</div>
          </div>
          {stepIdx < STEPS.length - 1 ? (
            <Button className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]"
                    disabled={!canNext()}
                    onClick={() => setStepIdx(i => Math.min(STEPS.length - 1, i + 1))}>
              Avanti <ChevronRight size={16} className="ml-1"/>
            </Button>
          ) : (
            <Button className="rounded-xl bg-[--brand-accent] hover:bg-[--brand-accent-hover] text-white"
                    onClick={handleAddToCart}>
              {editingItem ? (
                <><Pencil size={16} className="mr-2"/>Salva</>
              ) : (
                <><ShoppingCart size={16} className="mr-2"/>Aggiungi</>
              )}
            </Button>
          )}
        </div>
      </main>
      <Footer />

      {/* Combo drink selection dialog */}
      <Dialog open={drinkDialog} onOpenChange={setDrinkDialog}>
        <DialogContent className="max-w-md" data-testid="builder-combo-drink-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wine size={16}/> Scegli la bevanda</DialogTitle>
            <DialogDescription>{combo && `${combo.name} · ${eur(combo.price)}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(combo?.drink_ids || []).map(id => {
              const d = drinks.find(x => x.id === id);
              if (!d) return null;
              const on = selectedDrinkId === id;
              return (
                <label key={id}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${on ? 'border-[--brand-primary] bg-[#F3FBF7]' : 'border-[--brand-border] bg-white hover:bg-[--brand-surface-2]'}`}
                  data-testid={`builder-drink-choice-${id}`}>
                  <input type="radio" checked={on} onChange={() => setSelectedDrinkId(id)} className="accent-[--brand-primary]"/>
                  {d.image_url && <img src={d.image_url} alt={d.name} className="w-12 h-12 rounded-lg object-cover"/>}
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{d.name}</div>
                    {d.description && <div className="text-xs text-[--brand-muted]">{d.description}</div>}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDrinkDialog(false)}>Indietro</Button>
            <Button onClick={confirmComboAddToCart} disabled={!selectedDrinkId} className="rounded-xl bg-[--brand-primary]" data-testid="builder-combo-confirm">
              <ShoppingCart size={14} className="mr-2"/>Aggiungi al carrello
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SizeGrid({ items, selectedId, onPick }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {items.map(ing => (
        <button key={ing.id}
          onClick={() => onPick(ing.id)}
          className={`relative text-left rounded-2xl border p-3 transition-shadow ${selectedId === ing.id ? 'border-[--brand-primary] ring-2 ring-[--brand-primary]/20 bg-[#F3FBF7]' : 'border-[--brand-border] bg-white hover:shadow-[var(--shadow-md)]'}`}
          data-testid={`builder-ingredient-size-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}>
          {ing.image_url && (
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[--brand-surface-2] mb-2">
              <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          )}
          <div className="font-semibold text-sm">{ing.name}</div>
          {ing.description && <div className="text-xs text-[--brand-muted] line-clamp-2">{ing.description}</div>}
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className="tabular-nums font-semibold text-[--brand-accent]">{eur(ing.price)}</span>
            {ing.max_total && <Badge variant="secondary" className="text-[10px] uppercase">Max {ing.max_total} ingredienti</Badge>}
          </div>
          {selectedId === ing.id && (
            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[--brand-primary] text-white flex items-center justify-center">
              <Check size={14}/>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function MultiGrid({ items, cat, labels, qtyOf, onAdd, onRemove, limit, used, maxQtyPerItem, totalLimit, totalUnits }) {
  const catLabel = (labels && labels[cat]) || cat;
  return (
    <div>
      <div className="mb-3 text-sm text-[--brand-muted] flex items-center gap-2 flex-wrap">
        <Info size={14}/>
        {limit != null ? (
          <span>Fino a <b>{limit}</b> {catLabel.toLowerCase()} · <span className={used === limit ? 'text-[--brand-accent] font-semibold' : ''}>{used}/{limit}</span></span>
        ) : (
          <span>Selezionane quanti ne vuoi ({used} selezionati)</span>
        )}
        {maxQtyPerItem > 1 && <span className="px-2 py-0.5 rounded-full bg-[--brand-surface-2] text-xs">Max {maxQtyPerItem}× stesso item</span>}
        {totalLimit != null && <span className={totalUnits >= totalLimit ? 'text-[--brand-accent] font-semibold' : ''}>· Totale poke: {totalUnits}/{totalLimit}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map(ing => {
          const qty = qtyOf(ing.id);
          const selected = qty > 0;
          return (
            <div key={ing.id}
              className={`relative rounded-2xl border p-3 transition-shadow ${selected ? 'border-[--brand-primary] ring-2 ring-[--brand-primary]/20 bg-[#F3FBF7]' : 'border-[--brand-border] bg-white'}`}
              data-testid={`builder-ingredient-${ing.category}-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {ing.image_url && (
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[--brand-surface-2] mb-2">
                  <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="font-semibold text-sm">{ing.name}</div>
              {ing.description && <div className="text-xs text-[--brand-muted] line-clamp-2">{ing.description}</div>}
              {ing.price > 0 && (
                <div className="text-xs tabular-nums font-semibold text-[--brand-accent] mt-1">+{eur(ing.price)}</div>
              )}
              {maxQtyPerItem > 1 && qty > 0 ? (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    onClick={() => onRemove(ing.id)}
                    className="h-8 w-8 rounded-full border border-[--brand-border] flex items-center justify-center bg-white hover:bg-[--brand-surface-2]"
                    aria-label="Riduci"
                    data-testid={`builder-qty-minus-${ing.category}-${ing.id}`}>
                    <Minus size={14}/>
                  </button>
                  <span className="font-bold tabular-nums text-[--brand-primary]" data-testid={`builder-qty-${ing.category}-${ing.id}`}>×{qty}</span>
                  <button
                    onClick={() => onAdd(ing.id)}
                    disabled={qty >= maxQtyPerItem}
                    className="h-8 w-8 rounded-full bg-[--brand-primary] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[--brand-primary-hover]"
                    aria-label="Aumenta"
                    data-testid={`builder-qty-plus-${ing.category}-${ing.id}`}>
                    <Plus size={14}/>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => selected ? onRemove(ing.id) : onAdd(ing.id)}
                  className={`mt-2 w-full rounded-xl py-2 text-sm font-semibold ${selected ? 'bg-[--brand-primary] text-white' : 'border border-[--brand-border] bg-white hover:bg-[--brand-surface-2]'}`}
                  data-testid={`builder-toggle-${ing.category}-${ing.id}`}>
                  {selected ? <><Check size={14} className="inline mr-1"/>Selezionato</> : 'Aggiungi'}
                </button>
              )}
              {selected && (
                <div className="absolute top-2 right-2 min-w-[24px] h-6 px-1.5 rounded-full bg-[--brand-primary] text-white text-xs font-bold flex items-center justify-center">
                  {qty > 1 ? `×${qty}` : <Check size={12}/>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ selectedSize, selection, ingredients, totalPrice, totalUnits, totalLimit, labels }) {
  const L = labels || CATEGORY_LABELS_DEFAULT;
  const aggregate = (cat) => {
    const counts = new Map();
    selection[cat].forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
    return [...counts.entries()].map(([id, q]) => {
      const ing = ingredients.find(i => i.id === id);
      return ing ? { name: ing.name, qty: q } : null;
    }).filter(Boolean);
  };
  return (
    <div className="rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
      <div className="text-xs uppercase tracking-widest text-[--brand-muted]">La tua Poke</div>
      <div className="font-display text-2xl font-bold mt-1">{selectedSize?.name || 'Da comporre'}</div>
      {totalLimit != null && (
        <div className="mt-1 text-xs">
          <span className={`px-2 py-0.5 rounded-full ${totalUnits >= totalLimit ? 'bg-[--brand-accent]/15 text-[--brand-accent]' : 'bg-[--brand-surface-2] text-[--brand-muted]'}`}>
            {totalUnits}/{totalLimit} ingredienti
          </span>
        </div>
      )}
      <div className="space-y-2 mt-3 text-sm">
        <Row label={L.base}    items={aggregate('base')} />
        <Row label={L.protein} items={aggregate('protein')} />
        <Row label={L.topping} items={aggregate('topping')} />
        <Row label={L.sauce}   items={aggregate('sauce')} />
        <Row label={L.extra}   items={aggregate('extra')} />
      </div>
      <div className="border-t border-[--brand-border] mt-4 pt-3 flex items-center justify-between">
        <span className="text-sm text-[--brand-muted]">Totale</span>
        <span className="font-display text-2xl font-bold tabular-nums text-[--brand-primary]" data-testid="builder-summary-total">{eur(totalPrice)}</span>
      </div>
    </div>
  );
}

function Row({ label, items }) {
  return (
    <div className="flex gap-2">
      <div className="w-20 shrink-0 text-[--brand-muted]">{label}</div>
      <div className="flex-1">
        {items.length ? (
          <div className="flex flex-wrap gap-1">
            {items.map((it, i) => (
              <span key={i} className="text-xs bg-[--brand-surface-2] px-2 py-0.5 rounded-full">
                {it.qty > 1 ? <b className="text-[--brand-primary]">{it.qty}× </b> : ''}{it.name}
              </span>
            ))}
          </div>
        ) : <span className="text-[--brand-muted]/70 text-xs">nessuno</span>}
      </div>
    </div>
  );
}

function SummaryStep({ selectedSize, selection, ingredients, totalPrice, notes, setNotes, labels }) {
  const totalUnits = ['base', 'protein', 'topping', 'sauce', 'extra'].reduce((s, c) => s + selection[c].length, 0);
  const totalLimit = selectedSize?.max_total == null ? null : Number(selectedSize.max_total);
  return (
    <div className="space-y-4">
      <SummaryCard selectedSize={selectedSize} selection={selection} ingredients={ingredients} totalPrice={totalPrice} totalUnits={totalUnits} totalLimit={totalLimit} labels={labels}/>
      <div>
        <label className="text-sm font-semibold">Note per la cucina (opzionale)</label>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Es. senza cipolla..." className="mt-1 rounded-xl" data-testid="builder-notes-textarea" />
      </div>
    </div>
  );
}
